import { useBundle } from '@amzn/react-arb-tools';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Text } from '@amzn/stencil-react-components/text';
import {
  AppLayout,
  AppLayoutHeader,
  AppLayoutMaxWidth,
  AppLayoutNav,
  Col,
  GridContainer,
  GridItem,
  Row,
} from '@amzn/stencil-react-components/layout';
import {
  SecondaryNav,
  SecondaryNavLink,
  SecondaryNavList,
} from '@amzn/stencil-react-components/secondary-nav';
import React, { PropsWithChildren } from 'react';
import { NAV_MESSAGE_BUNDLE_NAME } from 'src/constants';
import { t } from '../helpers';
import amazonLogo from '../images/amazon.png';
import amazonLogoWhiteText from '../images/amazon-white-text.png';
import gtcLogo from '../images/growthriveconnect.png';
import socialFacebook from '../images/social-facebook.svg';
import socialLinkedin from '../images/social-linkedin.svg';
import socialX from '../images/social-x.svg';
import socialYoutube from '../images/social-youtube.svg';
import {
  Link,
  LinkTextDecorationOptions,
} from '@amzn/stencil-react-components/link';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';

const footerReferences = [
  {
    reference: {
      text: 'grow-footer-ref',
    },
    references: [
      {
        text: 'grow-footer-ref-1',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'grow-footer-ref-2',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'grow-footer-ref-3',
        link: 'https://www.aboutamazon.com/about-us',
      },
    ],
  },
  {
    reference: {
      text: 'thrive-footer-ref',
    },
    references: [
      {
        text: 'thrive-footer-ref-1',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'thrive-footer-ref-2',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'thrive-footer-ref-3',
        link: 'https://www.aboutamazon.com/about-us',
      },
    ],
  },
  {
    reference: {
      text: 'connect-footer-ref',
    },
    references: [
      {
        text: 'connect-footer-ref-1',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'connect-footer-ref-2',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'connect-footer-ref-3',
        link: 'https://www.aboutamazon.com/about-us',
      },
    ],
  },
  {
    reference: {
      text: 'about-footer-ref',
    },
    references: [
      {
        text: 'about-footer-ref-1',
        link: 'https://www.aboutamazon.com/about-us',
      },
      {
        text: 'about-footer-ref-2',
        link: 'https://www.aboutamazon.com/about-us',
      },
    ],
  },
];

const FOOTER_SOCIAL_IMAGES = [
  {
    key: 'social-facebook',
    alt: 'social_facebook_logo_alt',
    src: socialFacebook,
    reference: 'https://www.aboutamazon.com/about-us',
  },
  {
    key: 'social-linkedin',
    alt: 'social_linkedin_logo_alt',
    src: socialLinkedin,
    reference: 'https://www.aboutamazon.com/about-us',
  },
  {
    key: 'social-x',
    alt: 'social_x_logo_alt',
    src: socialX,
    reference: 'https://www.aboutamazon.com/about-us',
  },
  {
    key: 'social-youtube',
    alt: 'social_youtube_logo_alt',
    src: socialYoutube,
    reference: 'https://www.aboutamazon.com/about-us',
  },
];

const GrowThriveConnectLayout = ({ children }: PropsWithChildren) => {
  const [bundle] = useBundle(NAV_MESSAGE_BUNDLE_NAME);

  const { matches } = useBreakpoints();

  function footerWrapper(
    logos: React.ReactNode[],
    disclaimers: React.ReactNode[],
  ): React.ReactNode {
    return matches.m || matches.s ? (
      <Col width={'100%'} alignItems="center" dataTestId="footer-small-test-id">
        <Row
          width={'100%'}
          gridGap={'dimensions.spacing.100'}
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.200',
            bottom: 'dimensions.spacing.200',
          }}
          justifyContent="center"
        >
          {logos}
        </Row>
        {disclaimers.map((text, index) => {
          return (
            <Col
              key={index}
              width={'100%'}
              gridGap={'dimensions.spacing.100'}
              alignItems="center"
              padding={{
                left: 'dimensions.spacing.500',
                right: 'dimensions.spacing.500',
                top: 'dimensions.spacing.200',
                bottom: 'dimensions.spacing.200',
              }}
            >
              {text}
            </Col>
          );
        })}
      </Col>
    ) : (
      <Row width={'100%'} dataTestId="footer-large-test-id">
        <Row
          width={'100%'}
          gridGap={'dimensions.spacing.100'}
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.400',
            bottom: 'dimensions.spacing.400',
          }}
        >
          {logos}
        </Row>
        <Row
          width={'100%'}
          gridGap={'dimensions.spacing.100'}
          justifyContent="flex-end"
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.400',
            bottom: 'dimensions.spacing.400',
          }}
        >
          {disclaimers}
        </Row>
      </Row>
    );
  }

  function socialImage(
    key: string,
    src: string,
    alt: string,
    reference: string,
  ) {
    return (
      <Link
        key={key}
        href={reference}
        target="_blank"
        rel="noopener noreferrer"
        textDecoration={LinkTextDecorationOptions.None}
      >
        <img
          src={src}
          alt={t(bundle, alt)}
          style={{
            maxWidth: '1.5rem',
            maxHeight: '1.5rem',
          }}
          loading="lazy"
        />
      </Link>
    );
  }

  return (
    <AppLayout>
      {(matches.xl || matches.l) && (
        <AppLayoutHeader
          dataTestId="app-layout-header-test-id"
          backgroundColor={'color.surface.bg-muted'}
        >
          <Row
            gridGap="S300"
            justifyContent="space-between"
            padding={{
              left: 'dimensions.spacing.500',
              right: 'dimensions.spacing.500',
              top: 'dimensions.spacing.200',
              bottom: 'dimensions.spacing.200',
            }}
          >
            <img
              src={gtcLogo}
              style={{
                width: '9.688rem',
                height: '2.625rem',
              }}
              alt={t(bundle, 'gtc_logo_alt')}
              loading="lazy"
            />
            <img
              src={amazonLogo}
              style={{
                width: '5.625rem',
                height: '1.875rem',
              }}
              alt={t(bundle, 'amazon_logo_alt')}
              loading="lazy"
            />
          </Row>
        </AppLayoutHeader>
      )}
      <AppLayoutNav
        backgroundColor={'color.surface.bg-muted'}
        style={{ position: 'sticky', top: '0px', zIndex: 100 }}
        maxWidth={AppLayoutMaxWidth.Full}
      >
        <SecondaryNav
          style={{ backgroundColor: token('color.surface.bg-muted') }}
        >
          <SecondaryNavList
            backgroundColor={'color.surface.bg-muted'}
            style={{ justifyContent: 'flex-start' }}
          >
            {[
              { ref: '#grow', text: t(bundle, 'grow_nav') },
              { ref: '#thrive', text: t(bundle, 'thrive_nav') },
              { ref: '#connect', text: t(bundle, 'connect_nav') },
            ].map((nav) => (
              <SecondaryNavLink key={nav.ref} href={nav.ref}>
                {nav.text}
              </SecondaryNavLink>
            ))}
          </SecondaryNavList>
        </SecondaryNav>
      </AppLayoutNav>
      {children}
      <Col width={'100%'}>
        <Col
          backgroundColor={'color.surface.bg-muted'}
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.400',
            bottom: 'dimensions.spacing.400',
          }}
        >
          <GridContainer>
            {footerReferences.map((footerReference) => {
              return (
                <GridItem
                  key={footerReference.reference.text}
                  lg={3}
                  md={4}
                  sm={2}
                >
                  <Col gridGap={'dimensions.spacing.100'}>
                    <Text
                      fontWeight={700}
                      fontSize={'0.875rem'}
                      margin={{ bottom: 'dimensions.spacing.200' }}
                    >
                      {t(bundle, footerReference.reference.text)}
                    </Text>
                    {footerReference.references.map((reference) => {
                      return (
                        <Link
                          key={reference.text}
                          href={reference.link}
                          textDecoration={LinkTextDecorationOptions.None}
                          margin={{ bottom: 'dimensions.spacing.200' }}
                        >
                          {t(bundle, reference.text)}
                        </Link>
                      );
                    })}
                  </Col>
                </GridItem>
              );
            })}
          </GridContainer>
        </Col>
        <Col
          backgroundColor={'#232f3e'}
          width={'100%'}
          gridGap="dimensions.spacing.100"
          alignItems="center"
        >
          {(matches.m || matches.s) && (
            <Row
              dataTestId="logo-footer-test-id"
              padding={'dimensions.spacing.400'}
            >
              <img
                src={amazonLogoWhiteText}
                style={{
                  maxWidth: '5.625rem',
                  maxHeight: '1.875rem',
                  width: 'auto',
                  height: 'auto',
                }}
                alt={t(bundle, 'amazon_logo_alt')}
                loading="lazy"
              />
            </Row>
          )}

          {footerWrapper(
            FOOTER_SOCIAL_IMAGES.map((social) =>
              socialImage(social.key, social.src, social.alt, social.reference),
            ),
            [
              <Link
                key="footer-privacy-notice"
                color="#ffffffff"
                href="https://www.aboutamazon.com/about-us"
                textDecoration={LinkTextDecorationOptions.None}
              >
                {t(bundle, 'footer_privacy_notice')}
              </Link>,
              <Text key="footer-affiliates" color="#ffffffff">
                {t(bundle, 'footer_affiliates')}
              </Text>,
            ],
          )}
        </Col>
      </Col>
    </AppLayout>
  );
};

export default GrowThriveConnectLayout;
